/* ==========================================================================
   Elite Fishin — product page
   <ef-product> wires the option controls to the variant, and <ef-gallery>
   swaps the main image. Both are loaded only by sections/main-product.liquid.

   Money is never formatted here. Every variant's price, SKU and stock line is
   rendered server-side by Liquid and simply toggled, so the `money` filter
   keeps its formatting and the currency app's own markup survives untouched.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     Description spec lines

     Merchants type specs into the description as `Label: Value` lines broken
     with <br>, all inside one <p>:

       BOM Skirt Size 602<br>Head Length: 3.8"<br>Head Weight: 8.5oz

     which renders as a wall of identical text. This lifts those into a real
     two-column grid so the label and the value read differently.

     Deliberately conservative — it only touches a paragraph where at least two
     lines actually look like `Label: Value`, and it never invents or drops
     text. Prose paragraphs are left exactly as written. The original markup is
     what ships in the HTML, so this changes presentation, not content.
     --------------------------------------------------------------------- */

  function formatSpecParagraphs(scope) {
    var paras = scope.querySelectorAll('[data-ef-desc] > p');

    Array.prototype.forEach.call(paras, function (p) {
      if (!p.querySelector('br')) return;

      // Split on <br>. The pattern has to allow attributes: this catalogue's
      // copy was pasted through TinyMCE, which writes <br data-mce-fragment="1">,
      // and a pattern of <br\s*/?> silently matches only the bare ones — which
      // merged four specs into two runs and dropped them back to prose.
      var lines = [];

      p.innerHTML.split(/<br\b[^>]*>/i).forEach(function (chunk) {
        var el = document.createElement('div');
        el.innerHTML = chunk;
        var text = (el.textContent || '')
          .replace(/\u00a0/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        if (text) lines.push(text);
      });

      if (lines.length < 2) return;

      var rows = lines.map(function (line) {
        var at = line.indexOf(':');
        if (at > 0 && at <= 40 && line.slice(at + 1).trim()) {
          var key = line.slice(0, at).trim();
          var value = line.slice(at + 1).trim();
          // A real label is a few words. A long value still carrying several
          // colons is a run that failed to split, so it stays whole rather
          // than being forced into a pairing that may be wrong.
          var wordy = key.split(/\s+/).length > 5;
          var looksMerged = value.length > 120 && (value.match(/:/g) || []).length > 1;
          if (!wordy && !looksMerged) return { key: key, value: value };
        }
        return { key: null, value: line };
      });

      var labelled = rows.filter(function (r) {
        return r.key;
      }).length;
      if (labelled < 2) return; // not a spec block — leave the paragraph alone

      var dl = document.createElement('dl');
      dl.className = 'ef-desc-specs';

      rows.forEach(function (row) {
        var wrap = document.createElement('div');
        wrap.className = 'ef-desc-specs__row' + (row.key ? '' : ' ef-desc-specs__row--full');

        // dt is always present so the <dl> stays valid even for unlabelled lines
        var dt = document.createElement('dt');
        dt.className = 'ef-desc-specs__k';
        if (row.key) dt.textContent = row.key;
        else dt.hidden = true;

        var dd = document.createElement('dd');
        dd.className = 'ef-desc-specs__v';
        dd.textContent = row.value;

        wrap.appendChild(dt);
        wrap.appendChild(dd);
        dl.appendChild(wrap);
      });

      p.parentNode.replaceChild(dl, p);
    });
  }

  /* ---------------------------------------------------------------------
     Spec table -> its own row

     On 274 of 732 active products the spec table lives inside
     `product.description`, under a heading the merchant wrote. It therefore
     renders in the left column with the prose, where it is half the page wide
     and has to scroll. This lifts each table, and the heading directly above
     it, into the full-width row after the two columns.

     The heading is moved rather than replaced with a fixed label, because the
     wording is not always "Specifications": across the catalogue it is
     "Specifications" (189), "Specifications & Models" (34), "Available
     Variants" (34), "Specifications & Variants" (9), and a handful of
     "Available Colors" / "Available Sizes". Imposing one label would misname 42
     of them.

     The table is usually wrapped in a bare <div> directly under the description
     body, so the walk goes up to that top-level block rather than moving the
     <table> out of its wrapper and leaving an empty div behind.

     Presentation only: nothing is added or dropped, only relocated.
     --------------------------------------------------------------------- */

  function hoistSpecTables(scope) {
    var row = scope.querySelector('[data-ef-specs-row]');
    var body = scope.querySelector('[data-ef-desc]');
    if (!row || !body) return;

    var tables = body.querySelectorAll('table');
    if (!tables.length) return;

    var moved = 0;

    Array.prototype.forEach.call(tables, function (table) {
      // The outermost element that still sits inside the description body.
      var block = table;
      while (block.parentElement && block.parentElement !== body) {
        block = block.parentElement;
      }
      // Guard: if the walk escaped, leave this table alone.
      if (block.parentElement !== body) return;

      var heading = block.previousElementSibling;
      if (heading && /^H[1-6]$/.test(heading.tagName)) row.appendChild(heading);

      row.appendChild(block);
      moved++;
    });

    if (moved) {
      var section = scope.querySelector('[data-ef-specs-section]');
      if (section) section.hidden = false;
    }
  }

  /* ---------------------------------------------------------------------
     Recognised description sections

     Merchants write the same handful of sections under wording that never
     settles. Across the catalogue: "Key Features" 118 times but also
     "Features & Benefits" 5, "Features:" once and four carrying an emoji;
     "Why It Works" 52 against eleven "Why Choose the <product>?"; "Perfect For
     Targeting:" 51 against "Recommended Target Species" 10. The heading level
     wanders too — mostly h3, but h2 on a handful, which alone made the same
     section render at two different sizes on two products.

     Tagging them lets one rule style all of them, so the sections look the same
     from product to product whatever they are called and whatever level they
     were written at. The wording itself is left exactly as the merchant wrote
     it — this classifies, it does not rename.

     Only headings inside the description are considered, and anything
     unrecognised is left completely alone, so a product with none of these
     sections is untouched.
     --------------------------------------------------------------------- */

  var SECTION_PATTERNS = [
    { key: 'features', test: /feature|benefit/i },
    { key: 'why', test: /^\s*why\b/i },
    { key: 'ideal', test: /ideal\s+for|perfect\s+for(?!\s+targeting)|best\s+for/i },
    { key: 'species', test: /target|species/i },
    { key: 'specs', test: /specification/i }
  ];

  function tagDescriptionSections(scope) {
    // includes the inline lure specs, which live in neither of the other two
    var bodies = scope.querySelectorAll(
      '[data-ef-desc], [data-ef-specs-row], .ef-pdp__specs .ef-desc__body'
    );

    Array.prototype.forEach.call(bodies, function (body) {
      var heads = body.querySelectorAll('h1, h2, h3, h4, h5, h6');

      Array.prototype.forEach.call(heads, function (head) {
        var text = (head.textContent || '').replace(/\s+/g, ' ').trim();
        if (!text) return;

        for (var i = 0; i < SECTION_PATTERNS.length; i++) {
          if (SECTION_PATTERNS[i].test.test(text)) {
            head.classList.add('ef-desc__section-head');
            head.setAttribute('data-ef-section', SECTION_PATTERNS[i].key);
            return;
          }
        }
      });
    });
  }

  /* ---------------------------------------------------------------------
     A recognised heading with nothing under it is WORSE than no heading.
     `.ef-desc__section-head` draws a rule beneath itself, so an empty one
     renders a titled divider over blank space — reported on the Ali'i Kai
     skipjack, where the merchant wrote `<h3>Why It Works</h3>` as the very last
     node of the description and never wrote the copy.

     Only headings this file already tagged are considered, so an unrecognised
     merchant heading is still left completely alone.

     Walks nextSibling rather than nextElementSibling because a section's copy
     is sometimes a bare text node between elements, and skipping text nodes
     would read that section as empty and hide a heading that has content.
     Media counts as content even though it carries no text.
     --------------------------------------------------------------------- */
  function hideEmptySections(scope) {
    // ALL headings in a description body, not just the ones tagged as known
    // sections. Widened 27 Aug after a catalogue scan: five empty headings were
    // still rendering because they are not one of the five recognised names —
    // "What's Included" twice, a heading repeating the product title, and TWO
    // that are literally <h3></h3> with no text at all. A heading with nothing
    // under it has no content by definition, whatever it is called.
    var bodies = scope.querySelectorAll(
      '[data-ef-desc], [data-ef-specs-row], .ef-pdp__specs .ef-desc__body'
    );
    var heads = [];

    Array.prototype.forEach.call(bodies, function (body) {
      Array.prototype.push.apply(heads, body.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    });

    heads.forEach(function (head) {
      var node = head.nextSibling;
      var filled = false;

      while (node) {
        if (node.nodeType === 1) {
          if (/^H[1-6]$/.test(node.tagName)) break;
          if ((node.textContent || '').replace(/[\s\u00a0]/g, '')) {
            filled = true;
            break;
          }
          if (node.querySelector && node.querySelector('img, table, video, iframe, picture, svg')) {
            filled = true;
            break;
          }
        } else if (node.nodeType === 3) {
          if ((node.nodeValue || '').replace(/[\s\u00a0]/g, '')) {
            filled = true;
            break;
          }
        }
        node = node.nextSibling;
      }

      if (!filled) {
        head.hidden = true;
        head.setAttribute('data-ef-section-empty', '');
      }
    });
  }

  /* ---------------------------------------------------------------------
     A <style> PASTED INTO A DESCRIPTION IS DOCUMENT-WIDE.

     HTML has no scoped styles, so a <style> the merchant pasted along with
     their copy restyles the WHOLE PAGE, not the description. 159 active
     products carry one. 170 of those blocks are harmless Excel residue
     (`td {border...}`), but five carry
     `a { text-decoration: none; color: #464feb; }` and that repaints every
     link on the page — measured on the Dahunter gaffs, the FOOTER link went
     from the theme's #E62945 underlined to #464feb with no underline.

     Scoped to the description containers so it can only ever remove CSS the
     merchant pasted. The theme's own {% style %} output lives in the section
     wrappers, OUTSIDE these containers, and is untouched — checked on that
     same page, which carries 7 <style> tags in the body of which exactly 1 is
     from the description.

     Removed rather than neutralised because there is no way to un-apply a rule
     that has already matched; the element has to go.
     --------------------------------------------------------------------- */
  function stripInjectedStyles(scope) {
    var bodies = scope.querySelectorAll(
      '[data-ef-desc], [data-ef-specs-row], .ef-pdp__specs .ef-desc__body'
    );

    Array.prototype.forEach.call(bodies, function (body) {
      var styles = body.querySelectorAll('style');
      Array.prototype.forEach.call(styles, function (el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    });
  }

  function initDescription() {
    // first — the sooner pasted CSS stops applying to the page, the better
    stripInjectedStyles(document);
    formatSpecParagraphs(document);
    hoistSpecTables(document);
    // after the hoist, so the Specifications heading is tagged wherever it ended up
    tagDescriptionSections(document);
    // after the tagging, because it only considers headings that were tagged
    hideEmptySections(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDescription);
  } else {
    initDescription();
  }

  // the theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', initDescription);

  if (!('customElements' in window)) return;

  /* ---------------------------------------------------------------------
     <ef-gallery> — main stage plus a thumbnail rail
     --------------------------------------------------------------------- */

  customElements.define(
    'ef-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-ef-slide]'));
        // Includes the tiles inside the dialog, which carry the same attribute,
        // so choosing an image there swaps the stage with no special case.
        this.thumbs = Array.from(this.querySelectorAll('[data-ef-thumb]'));

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            this.show(thumb.getAttribute('data-ef-thumb'));
          });
        });

        this.wireDialog();
      }

      /* The + tile opens every image. <dialog> is used rather than a hand-rolled
         overlay because it brings focus trapping, Escape to close and inertness
         of the page behind it for free. If the element is unsupported the button
         simply does nothing — the row still works and nothing is hidden that was
         not already reachable from it. */
      wireDialog() {
        var dialog = this.querySelector('[data-ef-gallery-dialog]');
        var opener = this.querySelector('[data-ef-gallery-open]');
        if (!dialog || !opener || typeof dialog.showModal !== 'function') return;

        opener.addEventListener('click', function () {
          dialog.showModal();
        });

        var closer = dialog.querySelector('[data-ef-gallery-close]');
        if (closer) {
          closer.addEventListener('click', function () {
            dialog.close();
          });
        }

        dialog.addEventListener('click', function (event) {
          // Clicking the backdrop: the event target is the dialog itself only
          // when the click misses everything inside it.
          if (event.target === dialog) {
            dialog.close();
            return;
          }
          // Picking an image closes too — the point of opening was to choose one.
          if (event.target.closest && event.target.closest('[data-ef-thumb]')) {
            dialog.close();
          }
        });
      }

      // `id` is a media id as a string; unknown ids are ignored so a variant
      // without its own image simply leaves the stage alone.
      show(id) {
        if (!id) return;
        var match = this.slides.some(function (s) {
          return s.getAttribute('data-ef-slide') === id;
        });
        if (!match) return;

        this.slides.forEach(function (slide) {
          var active = slide.getAttribute('data-ef-slide') === id;
          slide.hidden = !active;
          // Anything hidden must leave the tab order with it.
          slide.querySelectorAll('a, button').forEach(function (el) {
            el.tabIndex = active ? 0 : -1;
          });
        });

        this.thumbs.forEach(function (thumb) {
          var active = thumb.getAttribute('data-ef-thumb') === id;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }
    }
  );

  /* ---------------------------------------------------------------------
     <ef-product> — option controls -> variant
     --------------------------------------------------------------------- */

  customElements.define(
    'ef-product',
    class extends HTMLElement {
      connectedCallback() {
        var data = this.querySelector('[data-ef-variants]');
        if (!data) return;

        try {
          this.variants = JSON.parse(data.textContent);
        } catch (e) {
          return; // malformed payload: leave the server-rendered state alone
        }

        this.gallery = this.querySelector('ef-gallery');
        this.idInput = this.querySelector('[data-ef-variant-id]');
        this.optionCount = Number(this.getAttribute('data-option-count') || 0);

        // Liquid ships this disabled so it cannot collide with the <noscript>
        // variant select. Reaching here means scripting works, so it takes over.
        if (this.idInput) this.idInput.disabled = false;

        this.onChange = this.onChange.bind(this);
        this.addEventListener('change', this.onChange);

        // Reflect whatever the server rendered, so availability marks are
        // correct on first paint rather than only after the first click.
        this.refreshAvailability(this.selectedOptions());
        // Same for the legend: a swatch grid cannot name the chosen value, so
        // "Color" would sit there unanswered until the shopper clicked something.
        this.syncLegends();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      selectedOptions() {
        var values = [];
        for (var i = 0; i < this.optionCount; i++) {
          var checked = this.querySelector('input[name="ef-option-' + i + '"]:checked');
          values.push(checked ? checked.value : null);
        }
        return values;
      }

      match(values) {
        return (
          this.variants.find(function (variant) {
            return values.every(function (value, i) {
              return value === null || variant.options[i] === value;
            });
          }) || null
        );
      }

      onChange(event) {
        if (!event.target.name || event.target.name.indexOf('ef-option-') !== 0) return;

        var values = this.selectedOptions();
        var variant = this.match(values);

        this.refreshAvailability(values);
        this.syncLegends();

        /* Whether the gallery follows depends on which option moved. A size does
           not change how a garment looks — every size of a shirt is the same
           photograph — but each variant still carries its own image, so picking
           XL swapped the shot for an identical one and looked like a glitch.
           Colour and model do change the object, so those still pull the
           gallery. Indexes come from data-visual-options. */
        var changedIndex = parseInt(event.target.name.replace('ef-option-', ''), 10);
        this.apply(variant, this.optionIsVisual(changedIndex));
      }

      optionIsVisual(index) {
        if (this._visual === undefined) {
          var raw = (this.getAttribute('data-visual-options') || '').trim();
          this._visual = raw
            ? raw.split(',').map(function (n) { return parseInt(n, 10); })
            : [];
        }
        return this._visual.indexOf(index) !== -1;
      }

      /* The legend names the current choice ("Rigging Fee: No Rigging Fee").
         Read it off the label rather than the input value, so it picks up the
         tidied text — several values in this catalogue trail a colon that
         Liquid strips for display but keeps on the value for matching. */
      syncLegends() {
        for (var i = 0; i < this.optionCount; i++) {
          var current = this.querySelector('[data-ef-opt-current="' + i + '"]');
          if (!current) continue;

          var checked = this.querySelector('input[name="ef-option-' + i + '"]:checked');
          if (!checked) continue;

          var label = this.querySelector('label[for="' + checked.id + '"]');
          // Prefer the marked name node. A tile's label also holds its price
          // delta, so the whole label read "100G+Dhs. 12.90" in the legend.
          var named = label && label.querySelector('[data-ef-opt-name]');
          if (named) current.textContent = named.textContent.trim();
          else current.textContent = label ? label.textContent.trim() : checked.value;
        }
      }

      /* Grey out any option value that cannot be reached from the other
         current selections. With one option — 87% of this catalogue — this is
         just "is there stock in that size". */
      refreshAvailability(values) {
        var self = this;

        for (var i = 0; i < this.optionCount; i++) {
          (function (index) {
            var inputs = self.querySelectorAll('input[name="ef-option-' + index + '"]');

            Array.prototype.forEach.call(inputs, function (input) {
              var probe = values.slice();
              probe[index] = input.value;

              var reachable = self.variants.some(function (variant) {
                return (
                  variant.available &&
                  probe.every(function (value, j) {
                    return value === null || variant.options[j] === value;
                  })
                );
              });

              input.classList.toggle('is-unavailable', !reachable);
              var label = input.closest('.ef-opt') || input.nextElementSibling;
              if (label) label.classList.toggle('is-unavailable', !reachable);
            });
          })(i);
        }
      }

      apply(variant, followMedia) {
        var id = variant ? String(variant.id) : '';

        if (this.idInput) this.idInput.value = id;

        // Price / SKU / stock: show the block belonging to this variant.
        ['price', 'sku', 'stock'].forEach((kind) => {
          var blocks = this.querySelectorAll('[data-ef-variant-' + kind + ']');
          Array.prototype.forEach.call(blocks, function (block) {
            block.hidden = block.getAttribute('data-ef-variant-' + kind) !== id;
          });
        });

        this.updateButton(variant);
        this.updateQuantityMax(variant);

        // `followMedia` is undefined on the initial pass from connectedCallback,
        // which must still set the right shot — only an explicit false skips it.
        if (followMedia !== false && variant && this.gallery && variant.featured_media) {
          this.gallery.show(String(variant.featured_media));
        }

        if (variant) this.syncUrl(variant.id);
      }

      // The ceiling belongs to the variant, not the product: rigging choices on
      // a one-of-a-kind lure each carry their own single unit, so switching
      // option must move the cap with it. `max` is absent when the variant is
      // untracked or set to keep selling — in that case remove it rather than
      // leaving a stale number behind, or the box silently caps at the last
      // variant's stock.
      updateQuantityMax(variant) {
        var input = this.querySelector('[data-ef-qty-input]');
        if (!input) return;

        var max = variant && typeof variant.max === 'number' ? variant.max : null;

        if (max && max > 0) {
          input.setAttribute('max', String(max));
          if ((parseInt(input.value, 10) || 1) > max) input.value = String(max);
        } else {
          input.removeAttribute('max');
        }
      }

      updateButton(variant) {
        var button = this.querySelector('[data-ef-add]');
        if (!button) return;

        var label = button.querySelector('[data-ef-add-label]') || button;
        var states = button.dataset;

        if (!variant) {
          button.disabled = true;
          label.textContent = states.labelUnavailable;
        } else if (!variant.available) {
          button.disabled = true;
          label.textContent = states.labelSoldOut;
        } else {
          button.disabled = false;
          label.textContent = states.labelAdd;
        }
      }

      // replaceState, not pushState: stepping through every size a shopper
      // tried is not a useful browser history.
      syncUrl(id) {
        if (!window.history || !window.history.replaceState) return;
        var url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  );
})();
